import './coapplicantUnemploymentDetailsPage.scss';

import KeyboardBackspaceRoundedIcon from '@mui/icons-material/KeyboardBackspaceRounded';
import MenuRoundedIcon from '@mui/icons-material/MenuRounded';
import LoadingButton from '@mui/lab/LoadingButton';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import red from '@mui/material/colors/red';
import LinearProgress from '@mui/material/LinearProgress';
import ThemeProvider from '@mui/material/styles/ThemeProvider';
import TextField from '@mui/material/TextField';
import { isEmpty, toString } from 'lodash';
import { FieldValues, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { NavigationPane } from '../../../components/shared_new/NavigationPane/NavigationPane';
import useCoapplicantStore from '../../../store/coapplicantStore/useCoapplicantStore';
import useNavigationPaneStore from '../../../store/navigationPaneStore/useNavigationPaneStore';
import useThemeStore from '../../../store/themeStore/useThemeStore';
import useWorkflowStore from '../../../store/workflowStore/useWorkflowStore';
import { locator } from '../../../testing/e2e-locator/locator';
import { inputEventHandler } from '../../../utils/component/inputEventHandler';

export function Component(): JSX.Element {
  const { createTheme } = useThemeStore();
  const theme = createTheme();
  const navigate = useNavigate();

  const { t } = useTranslation();
  const { next, previous } = useWorkflowStore();
  const { setDrawerOpen } = useNavigationPaneStore();

  const {
    computed: { isCoapplicantPersisted },
    coapplicantCurrentIncomeGrossAnnual,
    isSyncingCoapplicant,
    syncingCoapplicantFailedMessage,
    setCoapplicantCurrentIncomeGrossAnnual,
    syncCoapplicant,
  } = useCoapplicantStore();

  const {
    register,
    handleSubmit,
    setValue,
    trigger,
    formState: { errors, isDirty, isValid },
  } = useForm({
    criteriaMode: 'firstError',
    defaultValues: {
      grossAnnualIncomeValue: inputEventHandler.currency.formatter(
        coapplicantCurrentIncomeGrossAnnual,
      ),
    },
    delayError: 200,
    mode: 'onBlur',
  });

  const previousStep = (): void => {
    navigate(previous());
  };

  const grossAnnualIncomeValidation = {
    setValueAs: (value: string): string =>
      inputEventHandler.currency.parser(value),
    required: t('validation.grossAnnualIncome.required'),
    min: {
      value: 0,
      message: t('validation.grossAnnualIncome.min', { min: '$0' }),
    },
  };

  const formSubmit = async (values: FieldValues): Promise<void> => {
    const { grossAnnualIncomeValue } = values;

    if (!grossAnnualIncomeValue) {
      return;
    }

    setCoapplicantCurrentIncomeGrossAnnual(grossAnnualIncomeValue);

    if (isCoapplicantPersisted && isDirty) {
      const synced = await syncCoapplicant();
      if (synced) {
        navigate(next());
      }

      return;
    }

    navigate(next());
  };

  return (
    <ThemeProvider theme={theme}>
      <Box
        className="coapplicant-unemployment-details-page"
        sx={theme.pageLayout}
      >
        <header
          data-testid={locator.pages.coapplicantUnemploymentDetails.header.root}
        >
          <button
            aria-label="Previous step"
            data-testid={
              locator.pages.coapplicantUnemploymentDetails.header
                .previousStepButton
            }
            type="button"
            onClick={previousStep}
          >
            <KeyboardBackspaceRoundedIcon />
          </button>
          <button
            aria-label="Toggle drawer"
            data-testid={
              locator.pages.coapplicantUnemploymentDetails.header.drawerButton
            }
            type="button"
            onClick={() => {
              setDrawerOpen(true);
            }}
          >
            <MenuRoundedIcon />
          </button>
        </header>

        <div className="progress-container" role="complementary">
          <LinearProgress
            aria-label="Application completed percentage"
            data-testid={
              locator.pages.coapplicantUnemploymentDetails.progress.root
            }
            value={useWorkflowStore.getState().computed.progress}
            variant="determinate"
          />
        </div>

        <main>
          <form
            data-testid={locator.pages.coapplicantUnemploymentDetails.form.root}
            noValidate
            onSubmit={handleSubmit(formSubmit)}
          >
            <div className="form-content">
              <h1
                data-testid={
                  locator.pages.coapplicantUnemploymentDetails.form.h1
                }
              >
                {t('CoapplicantUnemploymentDetailsPage.title')}
              </h1>
              <h2
                data-testid={
                  locator.pages.coapplicantUnemploymentDetails.form.h2
                }
              >
                {t('CoapplicantUnemploymentDetailsPage.subtitle')}
              </h2>

              {!isEmpty(syncingCoapplicantFailedMessage) && (
                <div style={{ marginBottom: '2rem' }}>
                  <Alert
                    data-testid={
                      locator.pages.coapplicantUnemploymentDetails.alert.error
                    }
                    icon={false}
                    severity="error"
                  >
                    {syncingCoapplicantFailedMessage}
                  </Alert>
                </div>
              )}

              <div style={{ marginBottom: '3rem' }}>
                <div>
                  <TextField
                    {...register(
                      'grossAnnualIncomeValue',
                      grossAnnualIncomeValidation,
                    )}
                    error={!isEmpty(errors.grossAnnualIncomeValue)}
                    inputProps={{
                      'data-testid':
                        locator.pages.coapplicantUnemploymentDetails.form
                          .grossAnnualIncomeInput,
                      inputMode: 'numeric',
                    }}
                    label={t(
                      'CoapplicantUnemploymentDetailsPage.grossAnnualIncome',
                    )}
                    type="text"
                    variant="outlined"
                    fullWidth
                    onBlur={inputEventHandler.currency.blur(
                      'grossAnnualIncomeValue',
                      setValue,
                      trigger,
                    )}
                    onChange={inputEventHandler.currency.change(
                      'grossAnnualIncomeValue',
                      setValue,
                    )}
                    onFocus={inputEventHandler.currency.focus(
                      'grossAnnualIncomeValue',
                      setValue,
                    )}
                    onKeyDown={inputEventHandler.number.keydown}
                    onPasteCapture={inputEventHandler.number.pasteCapture}
                  />

                  {!isEmpty(errors.grossAnnualIncomeValue) && (
                    <span
                      data-testid={
                        locator.pages.coapplicantUnemploymentDetails.form
                          .grossAnnualIncomeInputError
                      }
                      style={{ fontSize: '12px', color: red[700] }}
                    >
                      {toString(errors.grossAnnualIncomeValue.message)}
                    </span>
                  )}
                </div>
              </div>

              <div style={{ marginBottom: '3rem' }}>
                <LoadingButton
                  color="primary"
                  data-testid={
                    locator.pages.coapplicantUnemploymentDetails.form
                      .submitButton
                  }
                  disabled={!isValid}
                  loading={isSyncingCoapplicant}
                  startIcon={<></>}
                  type="submit"
                  variant="contained"
                  fullWidth
                >
                  {t('CoapplicantUnemploymentDetailsPage.continue')}
                </LoadingButton>
              </div>
            </div>
            <footer
              data-testid={
                locator.pages.coapplicantUnemploymentDetails.footer.root
              }
            >
              {t('copyright', { year: new Date().getFullYear() })}
            </footer>
          </form>
        </main>
      </Box>

      <NavigationPane withDrawer withUserNameHeader />
    </ThemeProvider>
  );
}

Component.displayName = 'CoapplicantUnemploymentDetailsPage';
